/**
 * The decisions half of the resolve form, as pure functions.
 *
 * `ResolveIngestForm` is the only caller. These live outside it because a
 * component that owns eight pieces of state, validates three rules, builds a
 * request body and renders a dozen conditional slots is one nobody can hold
 * in their head — Sonar measured it at a cognitive complexity of 21 against a
 * limit of 15. Validation and body-building have no React in them, so they
 * belong here where a spec can exercise them directly.
 */
import type {
  ResolutionDecision,
  ResolutionDescription,
} from '@/features/SalesforceIngest/lib/describeResolution'
import { describeResolution } from '@/features/SalesforceIngest/lib/describeResolution'
import type { PayloadParseResult } from '@/features/SalesforceIngest/lib/parsePayloadBlock'
import type {
  SalesforceOpportunityIngestRequest,
  SalesforceOpportunityResolveRequest,
} from '@/generated/http-clients/v3ApiSchemas'

/** Field-level messages, held as i18n key suffixes rather than copy. */
export type FieldErrors = {
  payload?: string
  orgId?: string
  reason?: string
}

/**
 * The three client-side rules, run on submit rather than per keystroke.
 *
 * No `orgId` format check: there is no org-id validator anywhere in `src`,
 * and inventing a pattern risks rejecting valid ids for no gain. The server
 * validates the whole schema synchronously and answers 400 before the 202.
 */
export function validateResolveForm(
  parsed: PayloadParseResult,
  decision: ResolutionDecision,
  reason: string,
): FieldErrors {
  const errors: FieldErrors = {}

  if (!parsed.ok) {
    errors.payload = parsed.reason
  }
  if (!decision.createNewOrg && decision.orgId.length === 0) {
    errors.orgId = 'required'
  }
  if (reason.trim().length === 0) {
    errors.reason = 'required'
  }

  return errors
}

/**
 * The request body: the §A payload from the paste, merged with the four
 * fields the form collects.
 *
 * `orgId` is *omitted*, not blanked, when a new organisation is created: the
 * contract ignores it in that case, and an empty string is a value the server
 * would have to decide about.
 */
export function buildResolveBody(
  payload: SalesforceOpportunityIngestRequest,
  decision: ResolutionDecision,
  reason: string,
): SalesforceOpportunityResolveRequest {
  return {
    ...payload,
    createNewOrg: decision.createNewOrg,
    createNewAccount: decision.createNewAccount,
    ...(decision.createNewOrg ? {} : { orgId: decision.orgId }),
    reason: reason.trim(),
  }
}

/**
 * The derived-case sentence, or `null` while there is nothing honest to say.
 *
 * Describable only once the paste has parsed *and* the case is complete. The
 * sentence names the owner and the licence total, neither of which exists
 * before the parse; and the two cases that deliver into a named organisation
 * interpolate `orgId`, so describing one before it is typed renders "into
 * organization ." with a hole where the answer goes. Creating a new
 * organisation needs no id, so that case describes itself straight away.
 */
export function describeIfComplete(
  parsed: PayloadParseResult,
  decision: ResolutionDecision,
): ResolutionDescription | null {
  if (!parsed.ok) {
    return null
  }
  if (!decision.createNewOrg && decision.orgId.length === 0) {
    return null
  }
  return describeResolution(decision, parsed.payload, parsed.licenceTotal)
}
